import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import * as Sentry from '@sentry/angular';
import { providePrimeNG } from 'primeng/config';

import { registerLocaleData } from '@angular/common';
import de from '@angular/common/locales/de';
import en from '@angular/common/locales/en';
import fr from '@angular/common/locales/fr';
import nl from '@angular/common/locales/nl';
import { ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';

import { AppService } from '@core/services';

import { PublicModule } from '@public/public.module';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import * as CoreComponents from './core/components';
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';

import { environment } from '@env';

const LaraLightBlue = definePreset(Lara, {
    semantic: {
        primary: {
            50: '{blue.50}',
            100: '{blue.100}',
            200: '{blue.200}',
            300: '{blue.300}',
            400: '{blue.400}',
            500: '{blue.500}',
            600: '{blue.600}',
            700: '{blue.700}',
            800: '{blue.800}',
            900: '{blue.900}',
            950: '{blue.950}',
        },
    },
});

@NgModule({
    declarations: [AppComponent, ...CoreComponents.components],
    imports: [
        Sentry.TraceModule,
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        SharedModule,
        PublicModule, // Don't lazy load Public since we always need it anyway and lazy loading just makes loading it slower.
        CoreModule.forRoot(),
        FontAwesomeModule,
    ],
    providers: [
        { provide: 'environment', useValue: environment },
        { provide: LOCALE_ID, deps: [AppService], useFactory: (service: AppService) => service.getCurrentLocale() },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        providePrimeNG({
            theme: {
                preset: LaraLightBlue,
                options: {
                    darkModeSelector: false || 'none',
                    cssLayer: {
                        name: 'primeng',
                        order: 'tw-base, primeng, tw-utilities, tw-components',
                    },
                },
            },
        }),
        provideAppInitializer(() => {
            const initializerFn = () => {
                inject(Sentry.TraceService);
            };
            return initializerFn();
        }),
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}

registerLocaleData(nl, 'nl');
registerLocaleData(fr, 'fr');
registerLocaleData(de, 'de');
registerLocaleData(en, 'en');
